/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import { getCheckoutState } from '@msdyn365-commerce/global-state';
import * as React from 'react';
import { IAddressAddItem, IAddressAddUpdateProps } from './common/components/address-add';
import { IAddressSelectItem, IAddressSelectProps } from './common/components/address-select';
import { IAddressShowItem, IAddressShowProps } from './common/components/address-show';
import { ICheckoutShippingAddressViewProps } from './dobbies-checkout-shipping-address';
import { CheckoutAccordionStateInput } from '../../themes/dobbies/actions/checkout-accordion-steps-state';
import { Address } from '@msdyn365-commerce/retail-proxy';

/**
 * Address show component.
 * @param param0 - Root param.
 * @param param0.AddressDetail - Address detail.
 * @param param0.items - IAddressShowItem[].
 * @returns - Address Node.
 */
const AddressShow: React.FC<IAddressShowProps> = ({
    AddressDetail,
    items,
    isAuthenticated,
    data,
    onEdit,
    onSubmit,
    list,
    addAddress,
    updateLoggedInAddress,
    viewProps,
    currentAddressToBeEdited,
    editAddressFromList,
    shippingCost,
    updateCartDeliverySpecification,
    isShippingAddressReady,
    missingFieldsMsg,
    postCodeErrorMsg,
    context,
    loading
}) => {
    const [isSecondScreen, setIsSecondScreen] = React.useState(false);
    const homeDelLines = data?.cart?.result?.cart?.CartLines?.filter((cl: any) => (!cl.FulfillmentStoreId ? true : false));
    const no_of_items =
        homeDelLines?.reduce((total: any, item: any) => {
            return total + (item.Quantity || 0);
        }, 0) ?? 0;

    const email = data?.checkout?.result?.shippingAddress?.Email;

    return (
        <>
            {!isSecondScreen ? (
                <div className='address-details'>
                    {!list && (
                        <>
                            <p className='noOfItems'>
                                {`${no_of_items} ${no_of_items && no_of_items > 1 ? 'items' : 'item'}`}

                                <span>
                                    {shippingCost
                                        ? `Delivery cost: ${shippingCost !== undefined &&
                                              context?.cultureFormatter.formatCurrency(shippingCost)}`
                                        : `Delivery cost: Free`}
                                </span>
                            </p>
                            <span>Please confirm your delivery address.</span>
                        </>
                    )}
                    <Node {...AddressDetail}>
                        {items &&
                            items?.map((item: IAddressShowItem, index: number) => {
                                const { key, description } = item;
                                const isNotCountry = key.toLowerCase() !== 'threeletterisoregionname';
                                const isSecondLast = index === items.length - 2;
                                return (
                                    <React.Fragment key={key}>
                                        {isNotCountry && <>{description}</>}
                                        {isSecondLast && email && (
                                            <p className='msc-address-detail__item msc-address-detail__item-email msc-address-detail__item-newline'>
                                                {email}
                                            </p>
                                        )}
                                    </React.Fragment>
                                );

                                // return;
                            })}

                        <button
                            className='address-edit'
                            onClick={e => {
                                e.preventDefault();
                                if (editAddressFromList && currentAddressToBeEdited) {
                                    editAddressFromList(currentAddressToBeEdited);
                                } else if (onEdit) {
                                    onEdit();
                                }
                            }}
                        >
                            {' '}
                            Edit
                        </button>
                        <div className='missing-fields-error'>{missingFieldsMsg ? missingFieldsMsg : ''}</div>
                        <div className='missing-fields-error'>{postCodeErrorMsg ? postCodeErrorMsg : ''}</div>
                    </Node>
                    {Boolean(isAuthenticated) && (
                        <>
                            <div className='add-select-btns'>
                                <button
                                    onClick={e => {
                                        e.preventDefault();
                                        if (addAddress) {
                                            addAddress();
                                        }
                                    }}
                                >
                                    Add new address
                                </button>
                                <button
                                    onClick={e => {
                                        e.preventDefault();
                                        if (onEdit) {
                                            onEdit();
                                        }
                                    }}
                                >
                                    Select other address
                                </button>
                            </div>
                        </>
                    )}
                    {!list && (
                        <button
                            className='hd-details-save'
                            onClick={e => {
                                e.preventDefault();
                                setIsSecondScreen(true);
                                let hasCnCProducts = false;
                                viewProps?.data.cart.result?.cart.CartLines?.map(cl => {
                                    if (cl.FulfillmentStoreId) {
                                        hasCnCProducts = true;
                                    }
                                });
                                if (window.location.href.includes('#checkout-step-1')) {
                                    viewProps?.context.actionContext.update(new CheckoutAccordionStateInput(), { currentStep: 3 });
                                } else if (hasCnCProducts) {
                                    viewProps?.context.actionContext.update(new CheckoutAccordionStateInput(), { currentStep: 2 });
                                } else {
                                    viewProps?.context.actionContext.update(new CheckoutAccordionStateInput(), { currentStep: 3 });
                                }
                                if (updateCartDeliverySpecification) {
                                    updateCartDeliverySpecification();
                                }
                            }}
                            disabled={!isShippingAddressReady || Boolean(postCodeErrorMsg) || loading}
                        >
                            Save & continue
                        </button>
                    )}
                </div>
            ) : (
                <div className='address-details address-details-second-screen'>
                    <button
                        className='address-edit'
                        onClick={e => {
                            e.preventDefault();
                            if (onEdit) {
                                onEdit();
                            }
                        }}
                    >
                        {' '}
                        Edit
                    </button>
                    <p className='noOfItems'>
                        {`${no_of_items} ${no_of_items && no_of_items > 1 ? 'items' : 'item'}`}
                        <span>
                            {shippingCost
                                ? `Delivery cost: ${shippingCost !== undefined && context?.cultureFormatter.formatCurrency(shippingCost)}`
                                : `Delivery cost: Free`}
                        </span>
                    </p>
                    <h6>Address</h6>

                    <Node {...AddressDetail}>
                        {items &&
                            items.map((item: IAddressShowItem, index: number) => {
                                const { key, description } = item;
                                const isNotCountry = key.toLowerCase() !== 'threeletterisoregionname';
                                const isSecondLast = index === items.length - 2;
                                return (
                                    <React.Fragment key={key}>
                                        {isNotCountry && <>{description}</>}
                                        {isSecondLast && email && (
                                            <p className='msc-address-detail__item msc-address-detail__item-email msc-address-detail__item-newline'>
                                                {email}
                                            </p>
                                        )}
                                    </React.Fragment>
                                );

                                // return;
                            })}
                    </Node>
                </div>
            )}
        </>
    );
};

/**
 * Address Select Component.
 * @param param0 - Root param.
 * @param param0.SelectAddress - Select address.
 * @param param0.addButton - Add button.
 * @param param0.items - IAddressSelectItem[].
 * @param param0.isShowSaveButton - Boolean.
 * @param param0.saveButton - Save button.
 * @param param0.isShowCancelButton - Boolean.
 * @param param0.cancelButton - Cancel button.
 * @returns - SelectAddress Node.
 */
const AddressSelect: React.FC<IAddressSelectProps> = props => {
    const { SelectAddress, addButton, items, isShowSaveButton, saveButton, list, onEdit, data, shippingCost, context } = props;

    const homeDelLines = data?.cart?.result?.cart.CartLines?.filter((cl: any) => (!cl.FulfillmentStoreId ? true : false));
    const no_of_items =
        homeDelLines?.reduce((total: any, item: any) => {
            return total + (item.Quantity || 0);
        }, 0) ?? 0;

    return (
        <Node {...SelectAddress}>
            <p className='noOfItems'>
                {`${no_of_items} ${no_of_items && no_of_items > 1 ? 'items' : 'item'}`}
                <span>
                    {shippingCost
                        ? `Delivery cost: ${shippingCost !== undefined && context?.cultureFormatter.formatCurrency(shippingCost)}`
                        : `Delivery cost: Free`}
                </span>
            </p>
            <span>Please confirm your delivery address.</span>
            {items &&
                items.map((item: IAddressSelectItem, index) => {
                    const SelectItem = item.SelectItem;
                    return (
                        <Node {...SelectItem} key={item.key}>
                            <label>
                                {item.input}
                                <span className='address__custom-radio'></span>
                                <AddressShow
                                    {...item.showItems}
                                    list={list}
                                    onEdit={onEdit}
                                    currentAddressToBeEdited={data.address.result[index]}
                                    editAddressFromList={props.editAddressFromList}
                                    context={context}
                                />
                            </label>
                        </Node>
                    );
                })}
            {addButton}
            {isShowSaveButton && saveButton}
            {/* {isShowCancelButton && cancelButton} */}
        </Node>
    );
};

/**
 * Address Add Update Component.
 * @param param0 - Root param.
 * @param param0.AddressForm - Address form.
 * @param param0.heading - Address heading.
 * @param param0.items - IAddressAddItem[].
 * @param param0.hasError - Boolean.
 * @param param0.error - Error message.
 * @param param0.isShowSaveButton - Boolean.
 * @param param0.saveButton - Save button.
 * @param param0.isShowCancelButton - Boolean.
 * @param param0.cancelButton - Cancel button.
 * @returns Address add update component node.
 */
const AddressAddUpdate: React.FC<IAddressAddUpdateProps & ICheckoutShippingAddressViewProps> = ({
    AddressForm,
    heading,
    items,
    hasError,
    error,
    isShowSaveButton,
    saveButton,
    isShowCancelButton,
    cancelButton,
    renderInputWithButton,
    data,
    shippingCost,
    context
}) => {
    const homeDelLines = data.cart.result?.cart.CartLines?.filter((cl: any) => (!cl.FulfillmentStoreId ? true : false));
    const no_of_items =
        homeDelLines?.reduce((total: any, item: any) => {
            return total + (item.Quantity || 0);
        }, 0) ?? 0;

    return (
        <Node {...AddressForm}>
            {heading}
            {/* VSI-Customization -- START Render input field for postcode and button  */}
            {renderInputWithButton}
            {/* VSI-Customization -- END  */}
            <p className='noOfItems'>
                {`${no_of_items} ${no_of_items && no_of_items > 1 ? 'items' : 'item'}`}
                <span>
                    {shippingCost
                        ? `Delivery cost: ${shippingCost !== undefined && context?.cultureFormatter.formatCurrency(shippingCost)}`
                        : `Delivery cost: Free`}
                </span>
            </p>
            <span>Please confirm your delivery address.</span>
            {/* <h6>Delivery Address</h6> */}
            {items &&
                items.map((item: IAddressAddItem) => {
                    const { AddressItem, key, label, alert, input } = item;
                    if (key.toLowerCase() !== 'threeletterisoregionname') {
                        return (
                            <Node {...AddressItem} key={key}>
                                {label}
                                {alert}
                                {input}
                            </Node>
                        );
                    } else {
                        return;
                    }
                })}
            {hasError && (
                <Node {...error.AddressError}>
                    {error.title}
                    {error.message}
                </Node>
            )}
            {isShowSaveButton && saveButton}
            {isShowCancelButton && cancelButton}
        </Node>
    );
};

/**
 * Checkout Shipping Address View Component.
 * @param props - Props.
 * @returns - CheckoutShippingAddress Module.
 */
const CheckoutShippingAddressView: React.FC<ICheckoutShippingAddressViewProps> = props => {
    const {
        CheckoutShippingAddress,
        // checkoutErrorRef,
        // alert,
        viewState,
        showAddress,
        showAddressSelect,
        showAddOrUpdateAddress,
        addAddress,
        updateLoggedInAddress
        // cartLineImages
    } = props;
    const data = props.data;
    const { onEdit, onSubmit } = props.moduleState;
    const { isAuthenticated } = props.context.request.user;
    const [deliveryCost, setDeliveryCost] = React.useState(0);

    // Once charges have been updated, calculate total delivery charges
    React.useEffect(() => {
        if (props.data.checkoutModulesSharedState.result?.isChargesUpdated) {
            getCheckoutState(props.context.actionContext).then(checkoutState => {
                let charges = 0;
                checkoutState.checkoutCart?.cart.ChargeLines?.map(chargeLine => {
                    charges += chargeLine.CalculatedAmount || 0;
                });
                setDeliveryCost(charges);
            });
        }
    }, [props.data.checkoutModulesSharedState.result]);

    const shipping = props.data.checkout.result?.shippingAddress;

    const requiredKeys: (keyof Address)[] = ['Name', 'City', 'Phone', 'Email', 'Street', 'ZipCode'];
    const isShippingAddressReady = () => {
        return !!shipping && requiredKeys.every(k => !!shipping[k]);
    };

    const missingFieldsMsg = () => {
        const missingFields = shipping ? requiredKeys.filter(key => !shipping[key]) : requiredKeys;
        const isShippingAddressReady = missingFields.length === 0 && shipping?.ZipCode && shipping?.ZipCode !== '';
        const shippingErrorMessage = isShippingAddressReady
            ? undefined
            : `A required field is missing. Please select edit and complete all required fields.`;
        const arePersonalDetailsBeingSaved =
            shipping?.ZipCode && shipping?.Street && !shipping?.Name && !shipping?.Email && !shipping.Phone;
        return !arePersonalDetailsBeingSaved && missingFields.length < requiredKeys.length ? shippingErrorMessage : undefined;
    };
    const postCodeErrorMsg = () => {
        const checkoutModulesSharedState = props.data.checkoutModulesSharedState.result;
        const isPostCodeInvalid = checkoutModulesSharedState?.invalidAddressForCartProducts && checkoutModulesSharedState?.notBtPostCode;
        return isPostCodeInvalid ? 'Post code is restricted for some products.' : undefined;
    };

    return (
        <Module {...CheckoutShippingAddress}>
            {/* {alert} */}
            {viewState.isShowAddress && (
                <AddressShow
                    {...showAddress}
                    {...props}
                    context={props.context}
                    onEdit={onEdit}
                    onSubmit={onSubmit}
                    isAuthenticated={isAuthenticated}
                    addAddress={addAddress}
                    updateLoggedInAddress={updateLoggedInAddress}
                    viewProps={props}
                    editAddressFromList={props.editAddressFromList}
                    shippingCost={deliveryCost}
                    updateCartDeliverySpecification={props.updateCartDeliverySpecification}
                    isShippingAddressReady={isShippingAddressReady()}
                    missingFieldsMsg={missingFieldsMsg()}
                    postCodeErrorMsg={postCodeErrorMsg()}
                    loading={props.loading}
                />
            )}
            {viewState.isShowAddresList && (
                <AddressSelect
                    list={true}
                    onEdit={onEdit}
                    {...showAddressSelect}
                    context={props.context}
                    isAuthenticated={isAuthenticated}
                    data={data}
                    editAddressFromList={props.editAddressFromList}
                    shippingCost={deliveryCost}
                />
            )}
            {viewState.isShowAddOrUpdateAddress && (
                <AddressAddUpdate {...showAddOrUpdateAddress} {...props} context={props.context} shippingCost={deliveryCost} />
            )}
        </Module>
    );
};

export default CheckoutShippingAddressView;
